/** @jsx jsx */

/** @jsxFrag React.Fragment */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import type { Search_ProductHit as SearchProductHit } from '__generated__/graphql-types-do-not-use';

import { Typography, Typography2, breakpoints } from '@coursera/cds-core';

import type {
  EntWebsiteBlockCourseSearchWidgetQuery,
  EntWebsiteCourseSearchWidgetFragment,
} from 'bundles/ent-website/__generated__/contentfulTypes';
import CourseSearch from 'bundles/ent-website/components/CourseSearch/CourseSearch';
import { NUMBER_OF_SEARCH_RESULTS } from 'bundles/ent-website/components/CourseSearch/constants';
// FIXME: existing import/no-cycle violations are excused to prevent seeing errors when modifying other parts of the same file; please fix it carefully
// eslint-disable-next-line import/no-cycle
import BlockLayout, { noImage } from 'bundles/ent-website/contentful/core/BlockLayout';
import Button from 'bundles/ent-website/contentful/core/ContentfulButton';
import { entWebsiteBlockCourseSearchWidgetQuery } from 'bundles/ent-website/queries/EntWebsiteCourseSearchWidget';
import withBlockQuery from 'bundles/ent-website/utils/withBlockQuery';
import type { BlockModuleInnerProps } from 'bundles/ent-website/utils/withBlockQuery';
import useSearchQuery from 'bundles/search-common/providers/useSearchQuery';

type Props = BlockModuleInnerProps<EntWebsiteCourseSearchWidgetFragment>;

export function CourseSearchWidget({ content, assembly }: Props) {
  const styles = {
    container: css`
      display: flex;
      flex-direction: column;
      align-items: center;
    `,
    searchWidgetWrapper: css`
      width: 100%;

      & > div > div > div {
        padding-top: 32px;
        padding-bottom: 32px;
        width: 100%;
      }
    `,

    headline: css`
      font-size: 48px !important;
      line-height: 100% !important;
      font-weight: 600 !important;
      margin-bottom: 24px;
      letter-spacing: -0.03em;

      ${breakpoints.down('xs')} {
        font-size: 40px !important;
      }
    `,

    searchResultMessage: css`
      font-size: 1.5rem !important;
      letter-spacing: -0.02em !important;
      line-height: 150% !important;
      margin-bottom: var(--cds-spacing-300);
    `,
  };
  const { headline, searchResultMessage, searchResultCta } = content;

  const [query, setQuery] = React.useState('');

  const searchParams = React.useMemo(
    () => ({
      id: 'entSearch',
      entityType: 'PRODUCTS' as const,
      limit: NUMBER_OF_SEARCH_RESULTS,
      query,
      maxValuesPerFacet: 1000,
    }),
    [query]
  );

  const { isLoading, results } = useSearchQuery({
    entSearch: searchParams,
  });

  const contentPanel = () => (
    <div css={styles.container}>
      <Typography variant="h2" css={styles.headline} color="inherit">
        {headline}
      </Typography>
      <div css={styles.searchWidgetWrapper}>
        <CourseSearch
          onSearchQueryChange={setQuery}
          searching={isLoading}
          results={(results?.[0]?.elements ?? []) as SearchProductHit[]}
        />
      </div>
      {query && (
        <>
          {searchResultMessage && (
            <Typography2 component="p" css={styles.searchResultMessage}>
              {searchResultMessage}
            </Typography2>
          )}
          {searchResultCta && (
            <Button component="a" variant="primary" href={searchResultCta.ctaTarget} module={content} position={0}>
              {searchResultCta.ctaText}
            </Button>
          )}
        </>
      )}
    </div>
  );
  return <BlockLayout assembly={noImage(assembly)} renderContent={contentPanel} />;
}

export const BlockCourseSearchWidget = withBlockQuery<
  EntWebsiteCourseSearchWidgetFragment,
  // @ts-expect-error TSMIGRATION-5.0 TS2344
  EntWebsiteBlockCourseSearchWidgetQuery
>(entWebsiteBlockCourseSearchWidgetQuery)(CourseSearchWidget);
